import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import { t } from 'i18next';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { CompanySelectorFormField } from '~components/FormFields/CompanySelectorFormField';
import { CompanyBasic } from '~hooks/useCompany/models';
import { ReportCategory } from '~hooks/useOmniReports/models';

import { ReportCategoriesList } from './ReportCategoriesList';
import { ReportCategoryContent } from './ReportCategoryContent';
import ReportCategoryDialog from './ReportCategoryDialog';
import useReportCategoryQuery from './useReportCategoryQuery';

const selectedCompanySchema = yup.object().shape({
  company: yup.mixed<CompanyBasic>().nullable(),
});
type SelectedCompanyDTO = yup.InferType<typeof selectedCompanySchema>;

const OmniReports = () => {
  // MUI Tabs value use false to indicate no report is selected
  const [selectedReportCategory, setSelectedReportCategory] = useState<string | boolean>(
    false,
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editReportCategory, setEditReportCategory] = useState<ReportCategory | null>(
    null,
  );

  const form = useForm<SelectedCompanyDTO>({
    resolver: yupResolver(selectedCompanySchema),
    mode: 'onSubmit',
    defaultValues: { company: null },
  });

  const companyId = form.watch('company')?.id ?? null;

  const reportCategoryQuery = useReportCategoryQuery(companyId);

  const handleNewReportCategory = (reportCategory: ReportCategory) => {
    reportCategoryQuery.setQuery((state) => ({
      ...state,
      data: [...state.data, reportCategory],
    }));
  };

  const handleUpdateReportCategory = (reportCategory: ReportCategory) => {
    reportCategoryQuery.setQuery((state) => ({
      ...state,
      data: state.data.map((category) =>
        category.id === reportCategory.id ? reportCategory : category,
      ),
    }));
  };

  const handleDeleteReportCategory = (category: ReportCategory) => {
    setSelectedReportCategory(false);
    reportCategoryQuery.setQuery((state) => ({
      ...state,
      data: state.data.filter((c) => c.id !== category.id),
    }));
  };

  return (
    <Box>
      <CompanySelectorFormField
        clearable
        control={form.control}
        errors={form.formState.errors}
        hideLabel
        inputProps={{ placeholder: `${t('form_fields.company_selector_hint')}` }}
        name="company"
        sx={{ width: '540px' }}
      />
      {companyId && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            maxWidth: '100%',
          }}
        >
          <ReportCategoriesList
            companyId={companyId}
            reportCategoryQuery={reportCategoryQuery}
            selectedReportCategory={selectedReportCategory}
            setSelectedReportCategory={setSelectedReportCategory}
            setIsDialogOpen={setIsDialogOpen}
            setEditReportCategory={setEditReportCategory}
          />

          <ReportCategoryContent
            selectedReportCategory={
              selectedReportCategory
                ? (reportCategoryQuery.data.find(
                    (category) => category.id === selectedReportCategory,
                  ) ?? null)
                : null
            }
            companyId={companyId}
          />
        </Box>
      )}
      {companyId && (
        <ReportCategoryDialog
          open={isDialogOpen}
          onClose={() => {
            setIsDialogOpen(false);
            setEditReportCategory(null);
          }}
          companyId={companyId}
          onSuccess={
            editReportCategory ? handleUpdateReportCategory : handleNewReportCategory
          }
          onDelete={handleDeleteReportCategory}
          defaultReportCategory={editReportCategory}
        />
      )}
    </Box>
  );
};

export default OmniReports;
